import React from 'react';
import {
  Clock,
  Package,
  Truck,
  CheckCircle,
  AlertCircle,
  XCircle,
  MapPin,
  Navigation,
  Home,
  Ban,
} from 'lucide-react';

// Pickup Status Enum
export const PICKUP_STATUS = {
  PENDING: 'pending',
  PICKUP_REQUESTED: 'pickup-requested',
  ASSIGNED: 'assigned',
  PICKED_UP: 'picked-up',
  ARRIVED_AT_FACILITY: 'arrived-at-facility',
  COMPLETED: 'completed',
} as const;

// Delivery Status Enum
export const DELIVERY_STATUS = {
  PENDING: 'pending',
  PLANNED_FOR_DELIVERY: 'planned-for-delivery',
  IN_ROUTE: 'in-route',
  DELIVERED: 'delivered',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
} as const;

// Legacy Status (for backward compatibility)
export const LEGACY_STATUS = {
  PENDING: 'pending',
  PICKUP_REQUESTED: 'pickup-requested',
  ASSIGNED: 'assigned',
  IN_TRANSIT: 'in-transit',
  DELIVERED: 'delivered',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
} as const;

// Combined status type
export type PickupStatus = typeof PICKUP_STATUS[keyof typeof PICKUP_STATUS];
export type DeliveryStatus = typeof DELIVERY_STATUS[keyof typeof DELIVERY_STATUS];
export type LegacyStatus = typeof LEGACY_STATUS[keyof typeof LEGACY_STATUS];
export type OrderStatus = PickupStatus | DeliveryStatus | LegacyStatus;

// Status color mapping
export const getStatusColor = (status: string): string => {
  const statusLower = status?.toLowerCase() || '';
  
  switch (statusLower) {
    // Pickup statuses
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'pickup-requested':
      return 'bg-blue-100 text-blue-800';
    case 'assigned':
      return 'bg-orange-100 text-orange-800';
    case 'picked-up':
      return 'bg-purple-100 text-purple-800';
    case 'arrived-at-facility':
      return 'bg-indigo-100 text-indigo-800';
    case 'completed':
      return 'bg-green-100 text-green-800';
    
    // Delivery statuses
    case 'planned-for-delivery':
      return 'bg-cyan-100 text-cyan-800';
    case 'in-route':
    case 'in-transit':
      return 'bg-purple-100 text-purple-800';
    case 'delivered':
      return 'bg-green-100 text-green-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    case 'cancelled':
      return 'bg-gray-100 text-gray-800';
    
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

// Status icon mapping
export const getStatusIcon = (status: string): React.ReactElement => {
  const statusLower = status?.toLowerCase() || '';
  
  switch (statusLower) {
    // Pickup statuses
    case 'pending':
      return <Clock className="w-4 h-4" />;
    case 'pickup-requested':
      return <Package className="w-4 h-4" />;
    case 'assigned':
      return <AlertCircle className="w-4 h-4" />;
    case 'picked-up':
      return <Truck className="w-4 h-4" />;
    case 'arrived-at-facility':
      return <Home className="w-4 h-4" />;
    case 'completed':
      return <CheckCircle className="w-4 h-4" />;
    
    // Delivery statuses
    case 'planned-for-delivery':
      return <MapPin className="w-4 h-4" />;
    case 'in-route':
    case 'in-transit':
      return <Navigation className="w-4 h-4" />;
    case 'delivered':
      return <CheckCircle className="w-4 h-4" />;
    case 'failed':
      return <XCircle className="w-4 h-4" />;
    case 'cancelled':
      return <Ban className="w-4 h-4" />;
    
    default:
      return <Clock className="w-4 h-4" />;
  }
};

// Status display name mapping
export const getStatusLabel = (status: string): string => {
  const statusLower = status?.toLowerCase() || '';
  
  switch (statusLower) {
    // Pickup statuses
    case 'pending':
      return 'Pending';
    case 'pickup-requested':
      return 'Pickup Requested';
    case 'assigned':
      return 'Assigned';
    case 'picked-up':
      return 'Picked Up';
    case 'arrived-at-facility':
      return 'Arrived at Facility';
    case 'completed':
      return 'Completed';
    
    // Delivery statuses
    case 'planned-for-delivery':
      return 'Planned for Delivery';
    case 'in-route':
      return 'In Route';
    case 'in-transit':
      return 'In Transit';
    case 'delivered':
      return 'Delivered';
    case 'failed':
      return 'Failed';
    case 'cancelled':
      return 'Cancelled';
    
    default:
      return status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
  }
};

// Pickup status options for dropdowns
export const PICKUP_STATUS_OPTIONS = [
  { value: PICKUP_STATUS.PENDING, label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  { value: PICKUP_STATUS.PICKUP_REQUESTED, label: 'Pickup Requested', color: 'bg-blue-100 text-blue-800' },
  { value: PICKUP_STATUS.ASSIGNED, label: 'Assigned', color: 'bg-orange-100 text-orange-800' },
  { value: PICKUP_STATUS.PICKED_UP, label: 'Picked Up', color: 'bg-purple-100 text-purple-800' },
  { value: PICKUP_STATUS.ARRIVED_AT_FACILITY, label: 'Arrived at Facility', color: 'bg-indigo-100 text-indigo-800' },
  { value: PICKUP_STATUS.COMPLETED, label: 'Completed', color: 'bg-green-100 text-green-800' },
];

// Delivery status options for dropdowns
export const DELIVERY_STATUS_OPTIONS = [
  { value: DELIVERY_STATUS.PENDING, label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  { value: DELIVERY_STATUS.PLANNED_FOR_DELIVERY, label: 'Planned for Delivery', color: 'bg-cyan-100 text-cyan-800' },
  { value: DELIVERY_STATUS.IN_ROUTE, label: 'In Route', color: 'bg-purple-100 text-purple-800' },
  { value: DELIVERY_STATUS.DELIVERED, label: 'Delivered', color: 'bg-green-100 text-green-800' },
  { value: DELIVERY_STATUS.FAILED, label: 'Failed', color: 'bg-red-100 text-red-800' },
  { value: DELIVERY_STATUS.CANCELLED, label: 'Cancelled', color: 'bg-gray-100 text-gray-800' },
];

// All status options (for admin pages)
export const ALL_STATUS_OPTIONS = [
  ...PICKUP_STATUS_OPTIONS,
  ...DELIVERY_STATUS_OPTIONS.filter(d => d.value !== DELIVERY_STATUS.PENDING), // Remove duplicate pending
];

// Check if status is a pickup status
export const isPickupStatus = (status: string): boolean => {
  return Object.values(PICKUP_STATUS).includes(status as PickupStatus);
};

// Check if status is a delivery status
export const isDeliveryStatus = (status: string): boolean => {
  return Object.values(DELIVERY_STATUS).includes(status as DeliveryStatus);
};

// Get next possible statuses based on current status
export const getNextStatuses = (currentStatus: string, type: 'pickup' | 'delivery' = 'pickup'): string[] => {
  const statusLower = currentStatus?.toLowerCase() || '';
  
  if (type === 'pickup') {
    switch (statusLower) {
      case 'pending':
        return ['pickup-requested'];
      case 'pickup-requested':
        return ['assigned', 'cancelled'];
      case 'assigned':
        return ['picked-up', 'cancelled'];
      case 'picked-up':
        return ['arrived-at-facility'];
      case 'arrived-at-facility':
        return ['completed'];
      case 'completed':
        return [];
      default:
        return [];
    }
  } else {
    switch (statusLower) {
      case 'pending':
        return ['planned-for-delivery'];
      case 'planned-for-delivery':
        return ['in-route', 'cancelled'];
      case 'in-route':
        return ['delivered', 'failed'];
      case 'delivered':
        return [];
      case 'failed':
        return ['in-route', 'cancelled'];
      default:
        return [];
    }
  }
};

